import PropTypes from 'prop-types';
import { useEffect } from 'react';

/* ------------------------------------- MATERIAL UI --------------------------------------- */
import { Autocomplete, Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';

/* ------------------------------------- REACT JS ------------------------------------------ */
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
/* ------------------------------------- DEPENDENCIES -------------------------------------- */
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import saveIcon from '@iconify/icons-eva/save-outline';

import { editItem } from '../../fetch';

/* ------------------------------------- COMPONENTS ---------------------------------------- */
import { groupsActions } from '../../store';
import { useTranslation } from '../../common/LocalizationProvider';
import CountrySelect from '../CountrySelect';
import { selectAerosArray, selectGroupsArray } from '../../createSelectorUtils';

MyClubForm.propTypes = {
  groupId: PropTypes.number
};

export default function MyClubForm({ groupId }) {
  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const group = useSelector(selectGroupsArray).find((item) => item.id === groupId);

  const aeros = useSelector(selectAerosArray);

  useEffect(() => {
    if (groupId === null || groupId === undefined || group === undefined)
      navigate('/myclub', { replace: true });
  }, [group, groupId, navigate]);

  const formik = useFormik({
    initialValues: group,
    onSubmit: async (values) => {
      const response = await editItem('groups', values.id, values);
      if (response.ok) {
        enqueueSnackbar(
          values.id !== undefined ? t('addClub_Snackbar_Change') : t('addClub_Snackbar_Add'),
          {
            variant: 'success'
          }
        );
        dispatch(groupsActions.update([await response.json()]));
        navigate('/myclub', { replace: true });
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
        setSubmitting(false);
      }
    }
  });
  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setSubmitting,
    setFieldValue
  } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            label={t('fields_Name')}
            {...getFieldProps('name')}
            value={getFieldProps('name').value || ''}
            error={Boolean(touched.name && errors.name)}
            helperText={touched.name && errors.name}
          />

          <TextField
            fullWidth
            autoComplete="address"
            label={t('fields_Address')}
            {...getFieldProps('adresse')}
            value={getFieldProps('adresse').value || ''}
            error={Boolean(touched.adresse && errors.adresse)}
            helperText={touched.adresse && errors.adresse}
          />
          <TextField
            fullWidth
            autoComplete="address"
            label={t('fields_ExtraAddress')}
            {...getFieldProps('adresseSuite')}
            value={getFieldProps('adresseSuite').value || ''}
            error={Boolean(touched.adresseSuite && errors.adresseSuite)}
            helperText={touched.adresseSuite && errors.adresseSuite}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              autoComplete="city"
              label={t('fields_City')}
              {...getFieldProps('ville')}
              value={getFieldProps('ville').value || ''}
              error={Boolean(touched.ville && errors.ville)}
              helperText={touched.ville && errors.ville}
            />

            <TextField
              fullWidth
              autoComplete="zipCode"
              label={t('fields_Zip')}
              type="number"
              {...getFieldProps('codePostal')}
              value={getFieldProps('codePostal').value || ''}
              error={Boolean(touched.codePostal && errors.codePostal)}
              helperText={touched.codePostal && errors.codePostal}
            />
          </Stack>

          <CountrySelect formik={formik} />

          <Autocomplete
            fullWidth
            {...getFieldProps('aero')}
            options={aeros}
            getOptionLabel={(option) =>
              option.oaci === undefined ? '' : `${option.oaci} - ${option.nom}`
            }
            value={aeros.find((aero) => aero.oaci === getFieldProps('aero').value) || null}
            renderInput={(params) => <TextField {...params} label={t('fields_Aerodrome')} />}
            onChange={(event, value) => setFieldValue('aero', value !== null ? value.oaci : 0)}
          />

          <TextField
            fullWidth
            autoComplete="telephone"
            type="number"
            label={t('fields_Phone')}
            {...getFieldProps('phone')}
            value={getFieldProps('phone').value || ''}
            error={Boolean(touched.phone && errors.phone)}
            helperText={touched.phone && errors.phone}
          />

          <TextField
            fullWidth
            autoComplete="email"
            label={t('fields_Email')}
            type="email"
            {...getFieldProps('email')}
            value={getFieldProps('email').value || ''}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            fullWidth
            autoComplete="website"
            label={t('fields_Website')}
            type="website"
            {...getFieldProps('siteWeb')}
            value={getFieldProps('siteWeb').value || ''}
          />

          <TextField
            fullWidth
            type="number"
            label={t('fields_Distance')}
            {...getFieldProps('distanceLimit')}
            value={getFieldProps('distanceLimit').value || ''}
            error={Boolean(touched.distanceLimit && errors.distanceLimit)}
            helperText={touched.distanceLimit && errors.distanceLimit}
          />

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            startIcon={<Icon icon={saveIcon} />}
          >
            {t('btn_Save')}
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
